<template>
  <div class="step-modal">

    <!-- Шапка шага -->
    <div class="step-header">
      <div class="step-header__left">
        <div class="step-header__name">{{ this.data.step_name }}</div>
        <div class="step-header__meta">
          <q-chip
            dense size="sm"
            :color="statusColor"
            text-color="white"
            :icon="statusIcon"
            :label="statusLabel"
            class="q-mr-xs"
          />
          <span v-if="this.data.start_date" class="text-caption text-grey-6 q-mr-sm">
            Начат: {{ this.data.start_date }}
          </span>
          <span v-if="this.data.complete_date" class="text-caption text-grey-6">
            Завершён: {{ this.data.complete_date }}
          </span>
        </div>
      </div>
      <div class="step-header__right">
        <div class="step-header__counter" v-if="conditionsSummary.total">
          <q-circular-progress
            :value="conditionsSummary.pct"
            size="42px"
            :thickness="0.2"
            :color="conditionsSummary.pct === 100 ? 'positive' : 'primary'"
            track-color="grey-3"
            class="q-mr-xs"
          />
          <div class="step-header__counter-text">
            <div class="text-weight-medium" style="font-size:13px">
              {{ conditionsSummary.met }} / {{ conditionsSummary.total }}
            </div>
            <div class="text-caption text-grey-6">условий</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Блок незавершённых условий (только для CURRENT) -->
    <div v-if="this.data.status_code === 'CURRENT' && blockedConditions.length" class="blocking-banner">
      <div class="blocking-banner__title">
        <q-icon name="lock" size="14px" class="q-mr-xs" />
        Не выполнено {{ blockedConditions.length }} из {{ conditionsSummary.total }} условий:
      </div>
      <div
        v-for="c in blockedConditions"
        :key="c.remont_step_condition_id"
        class="blocking-banner__item"
      >
        <q-icon name="cancel" color="negative" size="14px" class="q-mr-xs flex-shrink-0" />
        <span class="text-caption">{{ c.comment || 'Условие ' + c.remont_step_condition_id }}</span>
      </div>
    </div>

    <!-- Вкладки -->
    <q-tabs v-model="tab" class="text-primary q-mt-sm" dense align="left">
      <q-tab v-for="item in tabs" :key="item.name" :name="item.name" :label="item.label" />
    </q-tabs>
    <q-separator />

    <CustomLoader v-if="loading" />
    <q-tab-panels v-else v-model="tab" animated>

      <!-- Условия -->
      <q-tab-panel :name="remontTimelineTabs.conditions" class="tab-panel q-pa-sm">
        <div v-if="!conditionsData.length" class="text-caption text-grey q-pa-md text-center">Нет условий</div>
        <template v-else>
          <div
            v-for="c in conditionsData"
            :key="c.remont_step_condition_id"
            :class="['cond-row', { 'cond-row--met': c.is_checked, 'cond-row--inactive': !c.is_active && !c.is_checked }]"
          >
            <q-icon
              :name="c.is_checked ? 'check_circle' : 'cancel'"
              :color="c.is_checked ? 'positive' : (c.is_active ? 'negative' : 'grey-4')"
              size="18px"
              class="q-mr-sm flex-shrink-0"
            />
            <div class="cond-row__text">
              <div class="text-caption text-weight-medium">{{ c.comment || 'Условие ' + c.remont_step_condition_id }}</div>
              <div v-if="c.checked_date" class="text-caption text-grey-5">{{ c.checked_date }}</div>
            </div>
            <q-space />
            <div v-if="!c.is_checked" class="cond-row__toggle" @click="toggleCondition(c)">
              <CustomLoader v-if="condLoading[c.remont_step_condition_id]" small />
              <q-icon
                v-else
                :name="c.is_active ? 'toggle_on' : 'toggle_off'"
                :color="c.is_active ? 'primary' : 'grey-4'"
                size="28px"
                class="cursor-pointer"
              />
              <q-tooltip>{{ c.is_active ? 'Деактивировать' : 'Активировать' }}</q-tooltip>
            </div>
          </div>
        </template>
      </q-tab-panel>

      <!-- Действия -->
      <q-tab-panel :name="remontTimelineTabs.actions" class="tab-panel q-pa-sm">
        <div v-if="!actionsData.length" class="text-caption text-grey q-pa-md text-center">Нет действий</div>
        <div
          v-for="a in actionsData"
          :key="a.remont_step_activity_id"
          :class="['action-row', { 'action-row--done': a.is_completed, 'action-row--inactive': !a.is_active && !a.is_completed }]"
        >
          <q-icon
            :name="a.is_completed ? 'check_circle' : 'radio_button_unchecked'"
            :color="a.is_completed ? 'positive' : (a.is_active ? 'grey-5' : 'grey-3')"
            size="18px"
            class="q-mr-sm flex-shrink-0"
          />
          <div class="action-row__text">
            <div class="text-caption text-weight-medium">{{ a.activity_name }}</div>
            <div class="text-caption text-grey-5">
              <span v-if="a.type_name">{{ a.type_name }}</span>
              <span v-if="a.fio"> · {{ a.fio }}</span>
              <span v-if="a.activity_date"> · {{ a.activity_date }}</span>
            </div>
          </div>
          <q-space />
          <div v-if="!a.is_completed" class="action-row__toggle" @click="toggleAction(a)">
            <CustomLoader v-if="actionLoading[a.remont_step_activity_id]" small />
            <q-icon
              v-else
              :name="a.is_active ? 'toggle_on' : 'toggle_off'"
              :color="a.is_active ? 'primary' : 'grey-4'"
              size="28px"
              class="cursor-pointer"
            />
          </div>
        </div>
      </q-tab-panel>

      <!-- Журнал событий (Phase 2) -->
      <q-tab-panel :name="remontTimelineTabs.eventLog" class="tab-panel q-pa-sm">
        <div class="text-caption text-grey-5 q-mb-xs" v-if="data.client_request_id">
          Заявка #{{ data.client_request_id }} · шаг {{ data.step_id }} · {{ eventLogRows.length }} событий
        </div>
        <div v-if="!data.client_request_id" class="text-caption text-grey q-pa-md text-center">
          Нет данных о заявке
        </div>
        <div v-else-if="eventLogLoading" class="text-center q-pa-md">
          <q-spinner color="primary" size="24px" />
        </div>
        <div v-else-if="!eventLogRows.length" class="text-caption text-grey q-pa-md text-center">
          Нет записей в журнале для этой заявки
        </div>
        <template v-else>
          <div
            v-for="ev in eventLogRows"
            :key="ev.event_log_id"
            :class="['ev-row', { 'ev-row--cascade': ev.source_code === 'CASCADE', 'ev-row--advanced': ev.is_completed, 'ev-row--error': ev.error_text }]"
            @click="toggleEventDetail(ev)"
          >
            <div class="ev-row__main">
              <q-icon
                :name="ev.is_completed ? 'check_circle' : (ev.error_text ? 'error' : 'pause_circle')"
                :color="ev.is_completed ? 'positive' : (ev.error_text ? 'negative' : 'grey-4')"
                size="16px"
                class="q-mr-sm flex-shrink-0"
              />
              <div class="ev-row__info" :style="{ paddingLeft: `${((ev.visual_depth ?? ev.event_depth) || 0) * 14}px` }">
                <span class="ev-row__step-name">{{ ev.step_name || ev.source_name || ev.source_code }}</span>
                <q-icon
                  v-if="ev.source_code === 'CASCADE'"
                  name="low_priority"
                  color="primary"
                  size="13px"
                  class="q-ml-xs"
                >
                  <q-tooltip>Каскадная проверка</q-tooltip>
                </q-icon>
                <span class="text-caption text-grey-5 q-ml-xs">{{ formatTime(ev.triggered_at) }}</span>
              </div>
              <span v-if="ev.error_text" class="text-caption text-negative q-ml-auto ellipsis-text">
                {{ ev.error_text.slice(0, 40) }}…
              </span>
              <q-icon
                :name="expandedEvent === ev.event_log_id ? 'expand_less' : 'expand_more'"
                size="16px" color="grey-5" class="q-ml-sm"
              />
            </div>

            <!-- Детали условий этого события, сгруппированные по шагу -->
            <div v-if="expandedEvent === ev.event_log_id" class="ev-row__conditions">
              <div v-if="eventCondLoading" class="text-center q-pa-xs"><q-spinner size="16px" /></div>
              <template v-else>
                <div
                  v-for="group in groupedConditions(expandedConditions)"
                  :key="group.step_id"
                  class="ev-cond-group"
                >
                  <div :class="['ev-cond-group__title', { 'ev-cond-group__title--current': group.step_id === data.step_id }]">
                    <q-icon
                      :name="group.step_id === data.step_id ? 'location_on' : 'flag'"
                      :color="group.step_id === data.step_id ? 'primary' : 'grey-5'"
                      size="12px"
                      class="q-mr-xs"
                    />
                    {{ group.step_name || 'Шаг ' + group.step_id }}
                    <q-badge
                      v-if="group.step_id === data.step_id"
                      color="primary"
                      label="текущий"
                      class="q-ml-xs"
                      style="font-size:9px"
                    />
                    <span :class="['q-ml-xs', group.items.filter(c => c.passed).length === group.items.length ? 'text-positive' : 'text-grey-5']">
                      {{ group.items.filter(c => c.passed).length }}/{{ group.items.length }}
                    </span>
                  </div>
                  <div
                    v-for="cc in group.items"
                    :key="cc.condition_check_id"
                    class="ev-cond"
                  >
                    <q-icon
                      :name="cc.passed ? 'check' : 'close'"
                      :color="cc.passed ? 'positive' : 'negative'"
                      size="14px"
                      class="q-mr-xs"
                    />
                    <span class="text-caption">{{ cc.condition_name }}</span>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </template>
      </q-tab-panel>

      <!-- История (лог условий) -->
      <q-tab-panel :name="remontTimelineTabs.logs" class="tab-panel q-pa-sm">
        <remont-timeline-logs :data="tabsData[remontTimelineTabs.logs]" />
      </q-tab-panel>

    </q-tab-panels>
  </div>
</template>

<script>
import {
  gerRemontTimelineStepData,
  getRemontTimelineCount,
  remontTimelineTabs,
  updateRemontConditionStepActivity,
  updateRemontActionStepActivity,
} from "@/components/modal/modals/remont-timeline-step/services";
import { remontTimelineStepAPI } from "@/components/modal/modals/remont-timeline-step/api";
import CustomLoader from "@/components/common/CustomLoader";
import RemontTimelineLogs from "@/components/modal/modals/remont-timeline-step/remont-timeline-logs";

const STATUS_MAP = {
  COMPLETED:   { label: 'Завершён',    color: 'positive', icon: 'check_circle' },
  CURRENT:     { label: 'В процессе',  color: 'primary',  icon: 'play_circle'  },
  NOT_STARTED: { label: 'Не начат',    color: 'grey-5',   icon: 'radio_button_unchecked' },
};

export default {
  name: "RemontTimelineStep",
  components: { CustomLoader, RemontTimelineLogs },
  props: ['data'],

  data() {
    return {
      loading: false,
      count: {},
      remontTimelineTabs: { ...remontTimelineTabs, eventLog: 'eventLog' },
      tab: remontTimelineTabs.conditions,
      tabsData: {},
      condLoading: {},
      actionLoading: {},
      eventLogRows: [],
      eventLogLoading: false,
      expandedEvent: null,
      expandedConditions: [],
      eventCondLoading: false,
    };
  },

  computed: {
    statusInfo() {
      return STATUS_MAP[this.data?.status_code] || STATUS_MAP.NOT_STARTED;
    },
    statusLabel() { return this.statusInfo.label; },
    statusColor() { return this.statusInfo.color; },
    statusIcon()  { return this.statusInfo.icon;  },

    conditionsData() {
      return this.tabsData[remontTimelineTabs.conditions]?.data || [];
    },
    actionsData() {
      return this.tabsData[remontTimelineTabs.actions]?.data || [];
    },
    conditionsSummary() {
      const all = this.conditionsData.filter(c => c.is_active);
      const met = all.filter(c => c.is_checked).length;
      return {
        total: all.length,
        met,
        pct: all.length ? Math.round(met / all.length * 100) : 0,
      };
    },
    blockedConditions() {
      return this.conditionsData.filter(c => c.is_active && !c.is_checked);
    },

    tabs() {
      const list = [
        { name: remontTimelineTabs.conditions, label: 'Условия' },
        { name: remontTimelineTabs.actions,    label: 'Действия' },
        { name: 'eventLog',                    label: 'Журнал событий' },
        { name: remontTimelineTabs.logs,       label: 'Лог проверок' },
      ];
      return list.map(item => {
        const cnt = this.count[item.name];
        return cnt ? { ...item, label: `${item.label} (${cnt})` } : item;
      });
    },
  },

  methods: {
    async loadTab(tabName) {
      this.loading = true;
      const res = await gerRemontTimelineStepData(this.data.remont_step_id, tabName);
      this.loading = false;
      if (res) this.tabsData = { ...this.tabsData, ...res };
    },
    async loadEventLog() {
      if (!this.data.client_request_id) return;
      this.eventLogLoading = true;
      try {
        const res = await remontTimelineStepAPI.getEventLog(
          this.data.client_request_id,
          this.data.step_id,
        );
        this.eventLogRows = Array.isArray(res) ? res : (res?.event_log || []);
        this.count = { ...this.count, eventLog: this.eventLogRows.length || undefined };
      } finally {
        this.eventLogLoading = false;
      }
    },
    async toggleEventDetail(ev) {
      if (this.expandedEvent === ev.event_log_id) {
        this.expandedEvent = null;
        return;
      }
      this.expandedEvent = ev.event_log_id;
      this.expandedConditions = [];
      this.eventCondLoading = true;
      try {
        const res = await remontTimelineStepAPI.getEventLogConditions(ev.event_log_id);
        this.expandedConditions = Array.isArray(res) ? res : (res?.conditions || []);
      } finally {
        this.eventCondLoading = false;
      }
    },
    async toggleCondition(c) {
      if (this.condLoading[c.remont_step_condition_id]) return;
      this.condLoading[c.remont_step_condition_id] = true;
      const res = await updateRemontConditionStepActivity(
        this.data.remont_step_id, c.remont_step_condition_id, { is_active: !c.is_active }
      );
      this.condLoading[c.remont_step_condition_id] = false;
      if (res) {
        this.tabsData = { ...this.tabsData, [remontTimelineTabs.conditions]: { data: res } };
        if (this.data?.updateData) await this.data.updateData();
      }
    },
    async toggleAction(a) {
      if (this.actionLoading[a.remont_step_activity_id]) return;
      this.actionLoading[a.remont_step_activity_id] = true;
      const res = await updateRemontActionStepActivity(
        this.data.remont_step_id, a.remont_step_activity_id, { is_active: !a.is_active }
      );
      this.actionLoading[a.remont_step_activity_id] = false;
      if (res) {
        this.tabsData = { ...this.tabsData, [remontTimelineTabs.actions]: { data: res } };
        if (this.data?.updateData) await this.data.updateData();
      }
    },
    groupedConditions(conditions) {
      const map = new Map();
      for (const c of conditions) {
        const key = c.step_id ?? 0;
        if (!map.has(key)) {
          map.set(key, { step_id: c.step_id, step_name: c.step_name, items: [] });
        }
        map.get(key).items.push(c);
      }
      return Array.from(map.values());
    },
    formatTime(val) {
      if (!val) return '';
      const d = new Date(String(val).replace(' ', 'T'));
      if (isNaN(d.getTime())) return val;
      return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    },
  },

  watch: {
    async tab(newTab) {
      if (newTab !== 'eventLog') {
        await this.loadTab(newTab);
      }
    },
  },

  async mounted() {
    await Promise.all([
      this.loadTab(remontTimelineTabs.conditions),
      getRemontTimelineCount(this.data?.remont_step_id).then(r => { if (r) this.count = r; }),
      this.loadEventLog(),
    ]);
  },
};
</script>

<style scoped lang="scss">
.step-modal {
  width: 760px;
  max-width: 100%;
  min-height: 300px;
}

/* Шапка */
.step-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 4px 0 8px;
  gap: 12px;

  &__name {
    font-size: 16px;
    font-weight: 600;
    line-height: 1.3;
    margin-bottom: 4px;
  }

  &__meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
  }

  &__right {
    flex-shrink: 0;
  }

  &__counter {
    display: flex;
    align-items: center;
  }

  &__counter-text {
    line-height: 1.2;
  }
}

/* Баннер блокирующих условий */
.blocking-banner {
  background: #fff5f5;
  border: 1px solid #fecaca;
  border-radius: var(--ms-radius-sm);
  padding: 8px 12px;
  margin-bottom: 4px;

  &__title {
    font-size: 12px;
    font-weight: 600;
    color: var(--ms-danger);
    margin-bottom: 4px;
    display: flex;
    align-items: center;
  }

  &__item {
    display: flex;
    align-items: center;
    padding: 2px 0;
    font-size: 12px;
    color: var(--ms-text);
  }
}

/* Вкладки */
.tab-panel {
  max-height: 380px;
  overflow-y: auto;
}

/* Строка условия */
.cond-row {
  display: flex;
  align-items: center;
  padding: 7px 6px;
  border-bottom: 1px solid #F3F4F6;
  border-radius: 4px;

  &:last-child { border-bottom: none; }

  &--met { background: #f0fdf4; }
  &--inactive { opacity: 0.45; }

  &__text { flex: 1; min-width: 0; }
  &__toggle { flex-shrink: 0; }
}

/* Строка действия */
.action-row {
  display: flex;
  align-items: center;
  padding: 7px 6px;
  border-bottom: 1px solid #F3F4F6;

  &:last-child { border-bottom: none; }
  &--done { background: #f0fdf4; }
  &--inactive { opacity: 0.45; }

  &__text { flex: 1; min-width: 0; }
  &__toggle { flex-shrink: 0; }
}

/* Журнал событий */
.ev-row {
  padding: 6px 6px;
  border-bottom: 1px solid #F3F4F6;
  cursor: pointer;
  border-radius: 4px;
  transition: background 0.1s;

  &:hover { background: #f9fafb; }
  &--cascade { background: #f8fbff; }
  &--advanced { background: #f0fdf4; }
  &--cascade.ev-row--advanced { background: #eef8ff; }
  &--error { background: #fff5f5; }

  &__main {
    display: flex;
    align-items: center;
  }

  &__info {
    flex: 1;
    min-width: 0;
  }

  &__step-name {
    font-size: 12px;
    font-weight: 600;
    color: #1f2937;
  }

  &__conditions {
    margin-top: 4px;
    margin-left: 26px;
    padding: 4px 8px;
    background: #f8fafc;
    border-radius: 6px;
  }
}

.ev-cond-group {
  margin-bottom: 6px;

  &:last-child { margin-bottom: 0; }

  &__title {
    display: flex;
    align-items: center;
    font-size: 11px;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    padding: 2px 0 3px;
    border-bottom: 1px solid #e5e7eb;
    margin-bottom: 3px;

    &--current {
      color: #0060fe;
      border-bottom-color: #bbdefb;
    }
  }
}

.ev-cond {
  display: flex;
  align-items: center;
  padding: 2px 0 2px 4px;
}

.ellipsis-text {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 160px;
  display: inline-block;
  vertical-align: middle;
}

.flex-shrink-0 { flex-shrink: 0; }
</style>
